/* Wraps Convex user settings query/mutation for convenient access across the app.
   Uses optimistic local overrides so toggles respond instantly. */

import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";

const DEFAULTS = {
  animationsEnabled: true,
  autoGenerateDailyDigest: false,
  analyzeContent: false,
  aiPodcastTranscription: false,
  autoSkipSegments: false,
  autoAnalyzeQueueItems: false,
  hideProvocativeContent: false,
  feedItemSize: "default" as "default" | "compact",
  tipsEnabled: true,
  tipsFrequency: "normal" as "normal" | "less" | "off",
} as const;

export function useUserSettings() {
  const settings = useQuery(api.userSettings.getSettings);
  const updateSingleSetting = useMutation(api.userSettings.updateSingleSetting);

  // Optimistic overrides: applied instantly, cleared when server value catches up
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  const prevSettingsRef = useRef(settings);

  // Clear overrides once the server value reflects the change
  useEffect(() => {
    if (settings && prevSettingsRef.current !== settings) {
      prevSettingsRef.current = settings;
      setOverrides((prev) => {
        const next: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(prev)) {
          // Keep override if server hasn't caught up yet
          if ((settings as Record<string, unknown>)[key] !== val) {
            next[key] = val;
          }
        }
        return Object.keys(next).length ? next : {};
      });
    }
  }, [settings]);

  function get<T>(key: string, fallback: T): T {
    if (key in overrides) return overrides[key] as T;
    if (settings) return ((settings as Record<string, unknown>)[key] as T) ?? fallback;
    return fallback;
  }

  const animationsEnabled = get("animationsEnabled", DEFAULTS.animationsEnabled);
  const autoGenerateDailyDigest = get("autoGenerateDailyDigest", DEFAULTS.autoGenerateDailyDigest);
  const analyzeContent = get("analyzeContent", DEFAULTS.analyzeContent);
  const aiPodcastTranscription = get("aiPodcastTranscription", DEFAULTS.aiPodcastTranscription);
  const autoSkipSegments = get("autoSkipSegments", DEFAULTS.autoSkipSegments);
  const autoAnalyzeQueueItems = get("autoAnalyzeQueueItems", DEFAULTS.autoAnalyzeQueueItems);
  const hideProvocativeContent = get("hideProvocativeContent", DEFAULTS.hideProvocativeContent);
  const feedItemSize = get("feedItemSize", DEFAULTS.feedItemSize) as "default" | "compact";
  const tipsEnabled = get("tipsEnabled", DEFAULTS.tipsEnabled);
  const tipsFrequency = get("tipsFrequency", DEFAULTS.tipsFrequency) as "normal" | "less" | "off";

  const updateSetting = useCallback((key: string, value: unknown) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
    void updateSingleSetting({ key, value });
  }, [updateSingleSetting]);

  return { settings, animationsEnabled, autoGenerateDailyDigest, analyzeContent, aiPodcastTranscription, autoSkipSegments, autoAnalyzeQueueItems, hideProvocativeContent, feedItemSize, tipsEnabled, tipsFrequency, updateSetting };
}
